import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { MutableRefObject } from 'react';

import { TextField } from '@mui/material';

import FavoriteAutocomplete from 'components/shared/FavoriteAutocomplete';
import { UserFavoriteReferenceType } from 'generated/index';

type Props<T> = {
  control: Control<any>;
  name: string;
  label: string;
  type: UserFavoriteReferenceType;
  variant?: 'standard' | 'outlined';
  size?: 'small' | 'medium';
  disabled?: boolean;
  disableFavoriteFilter?: boolean;
  disableSorting?: boolean;
  disableClearable?: boolean;
  options: T[];
  suggestedOptions?: T[];
  idProperty?: string;
  optionLabelProp?: string | string[];
  sortByProp?: string;
  isFavoriteProp?: string;
  fullWidth?: boolean;
  onChange?: (selectedOption: T) => void;
  onShowMore?: () => void;
  onClose?: () => void;
  showMoreButton?: boolean;
  showMoreText?: string;
  autoFocus?: boolean;
  innerRef?: MutableRefObject<HTMLInputElement | null>;
};

export default function ControlledFavoriteAutocomplete<T>({
  control,
  name,
  label,
  type,
  options,
  suggestedOptions,
  idProperty,
  optionLabelProp,
  sortByProp,
  isFavoriteProp,
  disableFavoriteFilter,
  disableSorting,
  disableClearable,
  variant = 'standard',
  size = 'small',
  disabled = false,
  fullWidth = false,
  onChange,
  onShowMore,
  onClose,
  showMoreButton,
  showMoreText,
  autoFocus = false,
  innerRef
}: Props<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <FavoriteAutocomplete
            type={type}
            disabled={disabled}
            idProperty={idProperty}
            isFavoriteProp={isFavoriteProp}
            optionLabelProp={optionLabelProp}
            sortByProp={sortByProp}
            disableSorting={disableSorting}
            disableFavoriteFilter={disableFavoriteFilter}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus={autoFocus}
                inputRef={innerRef}
                label={label}
                fullWidth={fullWidth}
                error={!!fieldState?.error?.type}
                helperText={fieldState?.error?.message}
                variant={variant}
                size={size}
              />
            )}
            options={options}
            suggestedOptions={suggestedOptions}
            disableClearable={disableClearable}
            value={field.value ?? null}
            onChange={(e, v) => {
              field.onChange(v);
              onChange?.(v);
            }}
            onShowMore={onShowMore}
            onClose={onClose}
            showMoreButton={showMoreButton}
            showMoreText={showMoreText}
          />
        );
      }}
    />
  );
}
